import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  BodyText,
  CaptionText,
  ActionMenu,
  Button,
  ShareFacebookIcon,
  ShareEmailIcon,
  ShareMessengerIcon,
  ShareWhatsappIcon,
  ShareTwitterIcon,
  ShareLinkIcon,
  CheckCircleAltIcon,
} from '@paypalcorp/pp-react';
import { StyleSheet, css } from '../../utility/paypalAphrodite';
import SocialShareEmail from './socialShareEmail';
import SocialShareMobile from './socialShareMobile';
import SocialShareFacebook from './socialShareFacebook';
import SocialShareMessenger from './socialShareMessenger';
import SocialShareWhatsapp from './socialShareWhatsapp';
import SocialShareTwitter from './socialShareTwitter';
import SocialShareCopyLink from './socialShareCopyLink';
import Illustration from '../illustrations/Illustration';
import CopyLink from '../CopyLink';
import { isInContext, isMobileOrTablet } from '../../utility/inContextDonation';
import { isExternalFlow } from '../../utility/productIntegrationUtils';
import { EVENT_NAME } from '../../utility/constants';
import { copyToClipboard } from '../../setup/lib/copyToClipBoard';
import templateData from '../../utility/getTemplateData';
import { trackLinkClick, trackPageIM } from '../../fpti/fpti';
import { FPTI_TAGS, LINK_NAME } from '../../fpti/fptiConstants';
import { v2black } from '@paypalcorp/pp-react-colors';

const styles = StyleSheet.create({
  socialContainer: {
    display: 'flex',
    ':nth-child(1n) > *': {
      marginRight: 10,
    },
  },
  socialContainerMobile: {
    marginLeft: 10,
  },
  shareMenu: {
    flexDirection: 'column',
  },
  copyLinkModal: {
    paddingBottom: 30,
  },
  copyLinkContainer: {
    margin: '0 0 60px',
  },
  copyLinkHeader: {
    color: v2black,
    marginTop: 20,
    marginBottom: 0,
    fontSize: '1.6rem',
  },
  copyLinkSubheader: {
    color: v2black,
    marginTop: 28,
    marginRight: 'auto',
    fontSize: '1.15rem',
  },
});

export default function SocialShare({
  shareUrl,
  socialCampaign,
  isMobile,
  ...props
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const encodedUrl = encodeURIComponent(shareUrl);
  const appId = '321983581337375';
  const emailUrl = `mailto:?subject=${socialCampaign.emailSubject}&body=${socialCampaign.emailBody}`;
  const facebookUrl = `https://www.facebook.com/dialog/share?app_id=${appId}&display=popup&quote=${socialCampaign.facebookMessage}&href=${encodedUrl}`;
  const fbMessengerUrl = isMobile
    ? `fb-messenger://share/?link=${encodedUrl}&app_id=${appId}&quote=${socialCampaign.facebookMessage}`
    : `https://www.facebook.com/dialog/send?app_id=${appId}&display=popup&link=${encodedUrl}&redirect_uri=${templateData.paypalHostName}`;
  const whatsappUrl = `https://${
    isMobile ? 'api' : 'web'
  }.whatsapp.com/send?&text=${socialCampaign.whatsappMessage}%20${encodedUrl}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${socialCampaign.twitterMessage}`;
  const socialShareAltText = socialCampaign.title;
  const isActionMenu = props.renderMode === 'sheet' ? true : false;
  const socialItems = isActionMenu ? generateMenuOptions() : [];

  function handleSocialLinkClick(url, type) {
    window.open(
      url,
      'popup',
      'toolbar=0,status=0,width=640,height=340,scrollable=true'
    );
    handleAnalytics(`social_share_${type}`);
  }

  function handleOpenCopyModal() {
    setIsModalOpen(true);
  }

  function handleMobileClick() {
    if (navigator.share) {
      navigator.share({
        title: socialCampaign.mobileTitle,
        text: socialCampaign.mobileText,
        url: encodedUrl,
      });
    } else {
      window.open(
        encodedUrl,
        'popup',
        'toolbar=0,status=0,width=640,height=340,scrollable=true'
      );
    }
    handleAnalytics('mobile_share');
  }

  function handleMenuSelection(index) {
    if (socialItems[index].type === 'copy_link') {
      copyToClipboard('copy-link-text');
      setIsCopied(true);
      handleAnalytics('copy_link');

      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } else {
      if (socialItems[index].type === 'email') {
        handleAnalytics('email-share');
        window.open(socialItems[index].url);
      } else if (socialItems[index].type === 'mobile_share') {
        handleMobileClick();
      } else {
        handleSocialLinkClick(
          socialItems[index].url,
          socialItems[index].title.toLowerCase()
        );
      }
      setIsCopied(false);
      setIsShareMenuOpen(false);
    }
  }

  function handleAnalytics(type) {
    if (props.analytics) {
      props.analytics(type);
    }
  }

  function generateMenuOptions() {
    let options = [];

    if (socialCampaign.email) {
      options.push({
        title: socialCampaign.sheet.email,
        url: emailUrl,
        type: 'email',
        itemLeft: (
          <ShareEmailIcon
            name="share-email"
            size="lg"
            style={{ color: props.color.email ?? props.color }}
          />
        ),
      });
    }
    if (socialCampaign.facebook) {
      options.push({
        title: socialCampaign.sheet.facebook,
        url: facebookUrl,
        type: 'facebook',
        itemLeft: (
          <ShareFacebookIcon
            name="share-facebook"
            size="lg"
            style={{ color: props.color.facebook ?? props.color }}
          />
        ),
      });
    }
    if (socialCampaign.twitter) {
      options.push({
        title: socialCampaign.sheet.twitter,
        url: twitterUrl,
        type: 'twitter',
        itemLeft: (
          <ShareTwitterIcon
            name="share-twitter"
            size="lg"
            style={{ color: props.color.twitter ?? props.color }}
          />
        ),
      });
    }
    if (socialCampaign.facebookMessenger) {
      options.push({
        title: socialCampaign.sheet.messenger,
        url: fbMessengerUrl,
        type: 'messenger',
        itemLeft: (
          <ShareMessengerIcon
            name="share-messenger"
            size="lg"
            style={{ color: props.color.facebookMessenger ?? props.color }}
          />
        ),
      });
    }
    if (socialCampaign.whatsapp) {
      options.push({
        title: socialCampaign.sheet.whatsapp,
        url: whatsappUrl,
        type: 'whatsapp',
        itemLeft: (
          <ShareWhatsappIcon
            name="share-whatsapp"
            size="lg"
            style={{ color: props.color.whatsapp ?? props.color }}
          />
        ),
      });
    }
    if (socialCampaign.copyLink) {
      options.push({
        title: (
          <>
            <span>{socialCampaign.sheet.copyLink}</span>
            {isCopied ? (
              <>
                <span className="sheetCopiedLabel">
                  <CheckCircleAltIcon size="xs" />
                  {socialCampaign.copyLinkCopied}
                </span>
              </>
            ) : null}
          </>
        ),
        type: 'copy_link',
        itemLeft: (
          <>
            <textarea
              id="copy-link-text"
              value={shareUrl}
              readOnly
              style={{
                position: 'absolute',
                opacity: 0.01,
                height: 0,
                overflow: 'hidden',
              }}
            >
              {shareUrl}
            </textarea>
            <ShareLinkIcon
              name="share-link"
              size="lg"
              style={{ color: props.color.copyLink ?? props.color }}
            />
          </>
        ),
      });
    }

    return options;
  }

  function handleMobileSocialShareButtonClick() {
    setIsShareMenuOpen(true);

    trackLinkClick(LINK_NAME.SOCIAL_SHARE, {
      eventName: EVENT_NAME.DW_GIVING_PAYMENT_SUCCESS_SHARE_PRESSED,
    });

    trackPageIM({
      pageSpec: {
        pgrp: window.fpti.pgrp,
        page: `${window.fpti.pgrp}:mobile::`,
      },
      additionalData: {
        [FPTI_TAGS.EVENT_NAME]: EVENT_NAME.DW_GIVING_SHARE_SCREEN_SHOWN,
      },
    });
  }

  function handleSocialShareSheetClose() {
    setIsShareMenuOpen(false);
    setIsCopied(false);
    trackLinkClick(LINK_NAME.SOCIAL_SHARE_DISMISS, {
      eventName: EVENT_NAME.DW_GIVING_SHARE_DISMISS_PRESSED,
    });
  }

  function mobileSocialShare() {
    return (
      <>
        <Button onClick={handleMobileSocialShareButtonClick}>
          {props.buttonText}
        </Button>
        <ActionMenu
          isOpen={isShareMenuOpen}
          onClose={handleSocialShareSheetClose}
          onSelection={(index) => handleMenuSelection(index)}
          options={socialItems}
        ></ActionMenu>
      </>
    );
  }

  function regularSocialShare() {
    return (
      <>
        {socialCampaign.email && (
          <SocialShareEmail
            url={emailUrl}
            handleAnalytics={handleAnalytics}
            linkTitle={socialShareAltText.email}
            color={props.color}
          />
        )}
        {navigator.share && socialCampaign.mobile && (
          <SocialShareMobile
            handleMobileClick={handleMobileClick}
            linkTitle={socialShareAltText.mobile}
            color={props.color}
          />
        )}
        {socialCampaign.facebook && (
          <SocialShareFacebook
            url={facebookUrl}
            handleSocialLinkClick={handleSocialLinkClick}
            linkTitle={socialShareAltText.facebook}
            color={props.color}
          />
        )}
        {socialCampaign.facebookMessenger && (
          <SocialShareMessenger
            url={fbMessengerUrl}
            handleSocialLinkClick={handleSocialLinkClick}
            linkTitle={socialShareAltText.messenger}
            color={props.color}
          />
        )}
        {socialCampaign.whatsapp && (
          <SocialShareWhatsapp
            url={whatsappUrl}
            handleSocialLinkClick={handleSocialLinkClick}
            linkTitle={socialShareAltText.whatsapp}
            color={props.color}
          />
        )}
        {socialCampaign.twitter && (
          <SocialShareTwitter
            url={twitterUrl}
            handleSocialLinkClick={handleSocialLinkClick}
            linkTitle={socialShareAltText.twitter}
            color={props.color}
          />
        )}
        {socialCampaign.copyLink && (
          <SocialShareCopyLink
            handleOpenCopyModal={handleOpenCopyModal}
            linkTitle={socialShareAltText.copyLink}
            color={props.color}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`${css(styles.socialContainer)} ${
          isActionMenu ? css(styles.shareMenu) : ''
        } ${isMobileOrTablet() ? css(styles.socialContainerMobile) : ''} `}
        style={{ justifyContent: props.align, ...props.style }}
        data-testid="social-share-container"
      >
        {isActionMenu ? mobileSocialShare() : regularSocialShare()}
      </div>
      {isInContext() && isExternalFlow() ? (
        <section>
          <CopyLink
            url={shareUrl}
            analytics={handleAnalytics}
            messages={{
              copy: socialCampaign.copyLinkCopy,
              copied: socialCampaign.copyLinkCopied,
            }}
          />
        </section>
      ) : null}
      {socialCampaign.copyLink && (
        <Dialog
          containerClassName="copy-link-modal"
          id="copyLinkModal"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <div
            className="ppvx_col-12 ppvx_d-flex"
            style={{ justifyContent: 'center' }}
          >
            <Illustration
              type="send_bubble_hearts"
              size="medium"
              ariaHidden={true}
            />
          </div>

          <BodyText className={`${css(styles.copyLinkHeader)} text-center`}>
            {socialCampaign.copyLinkHeader}
          </BodyText>
          <CaptionText
            className={`${css(styles.copyLinkSubheader)} text-center`}
          >
            {socialCampaign.copyLinkSubheader}
          </CaptionText>
          <section className={`${css(styles.copyLinkContainer)}`}>
            <div className={`${css(styles.copyLinkFieldContainer)}`}>
              <CopyLink
                url={shareUrl}
                analytics={handleAnalytics}
                messages={{
                  copy: socialCampaign.copyLinkCopy,
                  copied: socialCampaign.copyLinkCopied,
                }}
              />
            </div>
          </section>
        </Dialog>
      )}
    </>
  );
}

SocialShare.propTypes = {
  shareUrl: PropTypes.string,
  socialCampaign: PropTypes.object,
};

SocialShare.defaultProps = {
  align: 'center',
  handleAnalytics: null,
};
